var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { startTransition, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Popover, TextField, Tooltip, Typography } from '@mui/material';
import FormatColorResetIcon from '@mui/icons-material/FormatColorReset';
import chroma from 'chroma-js';
import { RgbaColorPicker } from 'react-colorful';
import { useTranslation } from 'react-i18next';
var MEMORY_COLORS_KEY = 'crqlar-new-color-picker-memory-colors';
var MAX_MEMORY_COLORS = 8;
var DEFAULT_COLOR = '#222222FF';
var TRANSPARENT_COLOR = 'transparent';
var TRANSPARENT_HEX = '#00000000';
var PRESET_COLORS = [
    TRANSPARENT_COLOR,
    '#000000',
    '#FFFFFF',
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#8B5CF6',
    '#6B7280',
];
var GERMAN_COLOR_ALIASES = {
    rot: 'red',
    dunkelrot: 'darkred',
    blau: 'blue',
    hellblau: 'lightblue',
    dunkelblau: 'darkblue',
    grün: 'green',
    gruen: 'green',
    hellgrün: 'lightgreen',
    hellgruen: 'lightgreen',
    dunkelgrün: 'darkgreen',
    dunkelgruen: 'darkgreen',
    gelb: 'yellow',
    orange: 'orange',
    lila: 'purple',
    violett: 'violet',
    rosa: 'pink',
    braun: 'brown',
    grau: 'gray',
    hellgrau: 'lightgray',
    dunkelgrau: 'darkgray',
    weiß: 'white',
    weiss: 'white',
    schwarz: 'black',
    türkis: 'turquoise',
    tuerkis: 'turquoise',
    durchsichtig: 'transparent',
};
var parseColor = function (color) {
    if (!color)
        return null;
    var normalizedName = color.trim().toLowerCase();
    var colorValue = GERMAN_COLOR_ALIASES[normalizedName] || normalizedName;
    if (colorValue === TRANSPARENT_COLOR)
        return { r: 0, g: 0, b: 0, a: 0 };
    try {
        var parsed = chroma(colorValue);
        var _a = parsed.rgb(), r = _a[0], g = _a[1], b = _a[2];
        return { r: r, g: g, b: b, a: parsed.alpha() };
    }
    catch (_b) {
        return null;
    }
};
var getEditablePickerColor = function (color) {
    if (color.a !== 0 || color.r !== 0 || color.g !== 0 || color.b !== 0) {
        return color;
    }
    var fallback = parseColor(DEFAULT_COLOR);
    return __assign(__assign({}, fallback), { a: 0 });
};
var getTransparentPickerColor = function (color) {
    if (color.r !== 0 || color.g !== 0 || color.b !== 0) {
        return __assign(__assign({}, color), { a: 0 });
    }
    var fallback = parseColor(DEFAULT_COLOR);
    return __assign(__assign({}, fallback), { a: 0 });
};
var rgbaToHex = function (_a) {
    var r = _a.r, g = _a.g, b = _a.b, a = _a.a;
    return (chroma(r, g, b).alpha(a).hex('rgba').toUpperCase());
};
// Transparency is useful authoring language; the persisted value remains an
// eight-digit hex color so existing API consumers keep the same value shape.
var rgbaToInputValue = function (color) { return (color.a === 0 ? TRANSPARENT_COLOR : rgbaToHex(color)); };
var rgbaToStoredValue = function (color) { return (color.a === 0 ? TRANSPARENT_HEX : rgbaToHex(color)); };
var normalizeHex = function (input) {
    try {
        return chroma(input).hex('rgba').toUpperCase();
    }
    catch (_a) {
        return null;
    }
};
var isTransparent = function (color) {
    var _a;
    if (!color)
        return false;
    return ((_a = parseColor(color)) === null || _a === void 0 ? void 0 : _a.a) === 0;
};
var transparentPattern = {
    bgcolor: '#fff',
    backgroundImage: 'linear-gradient(45deg, #cfd6df 25%, transparent 25%), linear-gradient(-45deg, #cfd6df 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #cfd6df 75%), linear-gradient(-45deg, transparent 75%, #cfd6df 75%)',
    backgroundSize: '10px 10px',
    backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0',
};
var smallTransparentPattern = __assign(__assign({}, transparentPattern), { backgroundSize: '8px 8px', backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0' });
var NewColorPicker = function (_a) {
    var value = _a.value, onChange = _a.onChange, _b = _a.isRequired, isRequired = _b === void 0 ? false : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, id = _a.id, _d = _a.placeholder, placeholder = _d === void 0 ? '#001B44' : _d, _e = _a.size, size = _e === void 0 ? 'small' : _e, _f = _a.variant, variant = _f === void 0 ? 'outlined' : _f, sx = _a.sx, _g = _a.presetColors, presetColors = _g === void 0 ? PRESET_COLORS : _g, _h = _a.showClear, showClear = _h === void 0 ? false : _h;
    var t = useTranslation().t;
    var _j = useState(null), anchorEl = _j[0], setAnchorEl = _j[1];
    var _k = useState(function () { return (getEditablePickerColor(parseColor(value) || parseColor(DEFAULT_COLOR))); }), internalColor = _k[0], setInternalColor = _k[1];
    var _l = useState(function () {
        var parsed = parseColor(value);
        return parsed ? rgbaToInputValue(parsed) : '';
    }), inputValue = _l[0], setInputValue = _l[1];
    var _m = useState([]), memoryColors = _m[0], setMemoryColors = _m[1];
    var isInternalChange = useRef(false);
    useEffect(function () {
        try {
            var stored = localStorage.getItem(MEMORY_COLORS_KEY);
            if (stored) {
                setMemoryColors(JSON.parse(stored));
            }
        }
        catch (_a) {
            setMemoryColors([]);
        }
    }, []);
    useEffect(function () {
        if (isInternalChange.current) {
            isInternalChange.current = false;
            return;
        }
        var parsed = parseColor(value);
        if (parsed) {
            setInternalColor(getEditablePickerColor(parsed));
            setInputValue(rgbaToInputValue(parsed));
            return;
        }
        setInputValue('');
        if (isRequired) {
            setInternalColor(parseColor(DEFAULT_COLOR));
        }
    }, [isRequired, value]);
    var emitChange = function (hex) {
        isInternalChange.current = true;
        onChange(hex);
    };
    var saveToMemory = function (newColor) {
        if (!newColor || isTransparent(newColor))
            return;
        var normalized = normalizeHex(newColor);
        if (!normalized)
            return;
        setMemoryColors(function (prev) {
            var updated = __spreadArray([
                normalized
            ], prev.filter(function (color) { return color.toUpperCase() !== normalized; }), true).slice(0, MAX_MEMORY_COLORS);
            try {
                localStorage.setItem(MEMORY_COLORS_KEY, JSON.stringify(updated));
            }
            catch (_a) {
                return updated;
            }
            return updated;
        });
    };
    var restoreCurrentValue = function () {
        var fallback = parseColor(value) || (isRequired ? parseColor(DEFAULT_COLOR) : null);
        if (!fallback) {
            setInputValue('');
            return;
        }
        setInternalColor(getEditablePickerColor(fallback));
        setInputValue(rgbaToInputValue(fallback));
    };
    var handleClose = function () {
        setAnchorEl(null);
        if (!inputValue) {
            restoreCurrentValue();
            return;
        }
        var parsed = parseColor(inputValue);
        if (parsed) {
            var storedValue = rgbaToStoredValue(parsed);
            setInputValue(rgbaToInputValue(parsed));
            saveToMemory(storedValue);
            return;
        }
        restoreCurrentValue();
    };
    var handleColorChange = function (newColor) {
        var rgbChanged = newColor.r !== internalColor.r ||
            newColor.g !== internalColor.g ||
            newColor.b !== internalColor.b;
        var nextColor = internalColor.a === 0 && newColor.a === 0 && rgbChanged
            ? __assign(__assign({}, newColor), { a: 1 }) : newColor;
        setInternalColor(nextColor);
        var storedValue = rgbaToStoredValue(nextColor);
        setInputValue(rgbaToInputValue(nextColor));
        startTransition(function () { return emitChange(storedValue); });
    };
    var handleInputChange = function (event) {
        var nextValue = event.target.value;
        setInputValue(nextValue);
        if (!nextValue) {
            if (!isRequired) {
                emitChange(undefined);
            }
            return;
        }
        var parsed = parseColor(nextValue);
        if (parsed) {
            setInternalColor(getEditablePickerColor(parsed));
            emitChange(rgbaToStoredValue(parsed));
        }
    };
    var handleInputBlur = function () {
        if (!inputValue) {
            restoreCurrentValue();
            return;
        }
        var parsed = parseColor(inputValue);
        if (!parsed)
            return;
        var storedValue = rgbaToStoredValue(parsed);
        setInternalColor(getEditablePickerColor(parsed));
        setInputValue(rgbaToInputValue(parsed));
        emitChange(storedValue);
    };
    var handlePresetClick = function (preset) {
        if (isTransparent(preset)) {
            setInternalColor(getTransparentPickerColor(internalColor));
            setInputValue(TRANSPARENT_COLOR);
            emitChange(TRANSPARENT_HEX);
            return;
        }
        var parsed = parseColor(preset);
        if (!parsed)
            return;
        setInternalColor(parsed);
        var hex = rgbaToHex(parsed);
        setInputValue(hex);
        emitChange(hex);
    };
    var clearColor = function () {
        setInputValue('');
        emitChange(undefined);
    };
    var parsedInputColor = parseColor(inputValue);
    var isInputValid = !inputValue || parsedInputColor !== null;
    var previewColor = parsedInputColor ? rgbaToStoredValue(parsedInputColor) : value;
    var hasPreviewColor = Boolean(previewColor);
    var showsTransparentColor = hasPreviewColor && isTransparent(previewColor);
    var swatchColor = hasPreviewColor && !showsTransparentColor && previewColor
        ? previewColor
        : 'transparent';
    return (_jsxs(_Fragment, { children: [_jsxs(Box, __assign({ sx: { position: 'relative', width: '100%' } }, { children: [_jsx(TextField, { id: id, fullWidth: true, size: size, variant: variant, value: inputValue, disabled: disabled, placeholder: placeholder, onChange: handleInputChange, onBlur: handleInputBlur, error: !isInputValid, sx: __assign(__assign({}, sx), { '& .MuiInputBase-input': {
                                pr: size === 'medium' ? '52px' : '44px',
                            } }) }), _jsx(IconButton, __assign({ "aria-label": t('Open color picker'), size: "small", disabled: disabled, onClick: function (event) { return setAnchorEl(event.currentTarget); }, sx: {
                            position: 'absolute',
                            zIndex: 1,
                            top: '50%',
                            right: 4,
                            width: size === 'medium' ? 40 : 32,
                            height: size === 'medium' ? 40 : 32,
                            p: 0,
                            borderRadius: 0.75,
                            transform: 'translateY(-50%)',
                        } }, { children: _jsx(Box, { component: "span", sx: __assign({ width: 24, height: 24, borderRadius: 0.75, border: '1px solid', borderColor: 'divider', bgcolor: swatchColor, backgroundClip: 'padding-box' }, (showsTransparentColor ? transparentPattern : {})) }) }))] })), _jsxs(Popover, __assign({ open: Boolean(anchorEl), anchorEl: anchorEl, onClose: handleClose, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, slotProps: { paper: { sx: { p: 1.5, width: 280 } } } }, { children: [_jsx(Box, __assign({ sx: {
                            '& .react-colorful': {
                                width: '100%',
                                height: 168,
                            },
                            '& .react-colorful__saturation': {
                                borderRadius: 1,
                                marginBottom: '12px',
                            },
                            '& .react-colorful__hue': {
                                height: 14,
                                borderRadius: 999,
                                marginBottom: '10px',
                            },
                            '& .react-colorful__alpha': {
                                height: 14,
                                borderRadius: 999,
                            },
                            '& .react-colorful__pointer': {
                                width: 20,
                                height: 20,
                            },
                        } }, { children: _jsx(RgbaColorPicker, { color: internalColor, onChange: handleColorChange }) })), _jsx(TextField, { fullWidth: true, size: "small", value: inputValue, onChange: handleInputChange, onBlur: handleInputBlur, placeholder: "#RGB, #RRGGBB, #RRGGBBAA", error: !isInputValid, sx: {
                            mt: 1,
                            '& input': {
                                fontFamily: 'monospace',
                            },
                        } }), _jsx(Typography, __assign({ variant: "body2", color: "text.secondary", sx: { mt: 1, display: 'block', lineHeight: 1.25 } }, { children: t('Presets') })), _jsxs(Box, __assign({ sx: { display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 } }, { children: [showClear && !isRequired && (_jsx(Tooltip, __assign({ title: t('Clear') }, { children: _jsx(IconButton, __assign({ "aria-label": t('Clear'), disabled: disabled, onClick: clearColor, size: "small", sx: {
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 0.5,
                                        height: 22,
                                        width: 22,
                                    } }, { children: _jsx(FormatColorResetIcon, { sx: { fontSize: 16 } }) })) }))), presetColors.map(function (preset) { return (_jsx(Box, { onClick: function () { return handlePresetClick(preset); }, sx: __assign(__assign({ width: 22, height: 22, borderRadius: 0.5, border: '1px solid', borderColor: 'divider', cursor: 'pointer', bgcolor: isTransparent(preset) ? TRANSPARENT_COLOR : preset }, (isTransparent(preset) ? smallTransparentPattern : {})), { '&:hover': { borderColor: 'primary.main' } }) }, preset)); })] })), memoryColors.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Typography, __assign({ variant: "body2", color: "text.secondary", sx: { mt: 1, display: 'block', lineHeight: 1.25 } }, { children: t('Recent') })), _jsx(Box, __assign({ sx: { display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 } }, { children: memoryColors.map(function (memoryColor, index) { return (_jsx(Box, { onClick: function () { return handlePresetClick(memoryColor); }, sx: {
                                        width: 22,
                                        height: 22,
                                        borderRadius: 0.5,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        cursor: 'pointer',
                                        bgcolor: memoryColor,
                                        '&:hover': { borderColor: 'primary.main' },
                                    } }, "".concat(memoryColor, "-").concat(index))); }) }))] }))] }))] }));
};
export default NewColorPicker;
